import { useMemo } from 'react';

import { type SizeSettingPreset, type SizeSettingSet } from '@air/api';

import { type SavedSize, type SizeSet } from '../utils/savedSizes';
import { usePrivateSizePresetsList } from './queries/usePrivateSizePresetsList';
import { usePrivateSizeSetsList } from './queries/usePrivateSizeSetsList';

export const toSavedSize = (sizePreset: SizeSettingPreset): SavedSize => ({
  height: sizePreset.height,
  id: sizePreset.id,
  name: sizePreset.name,
  width: sizePreset.width,
});

export const toSizeSet = (apiSizeSet: SizeSettingSet): SizeSet => ({
  id: apiSizeSet.id,
  members: apiSizeSet.members,
  name: apiSizeSet.name,
  updatedAt: apiSizeSet.updatedAt,
});

export type UseSavedSizesCatalogParams = {
  /** Suppresses the list queries when the rollout flag is off, so a disabled workspace never calls the endpoints. */
  enabled?: boolean;
  staleTime?: number;
  workspaceId: string | undefined;
};

export type UseSavedSizesCatalogResult = {
  /** True when either list query has failed -- an empty catalog here is unresolved, not authoritative. */
  isError: boolean;
  isLoading: boolean;
  /** Presets query alone, so a consumer that only cares about presets (e.g. reconciling a stale preset selection) isn't blocked by the unrelated sets query. */
  isSizePresetsError: boolean;
  isSizePresetsLoading: boolean;
  /** Sets query alone, so a consumer that only cares about sets isn't blocked by the unrelated presets query. */
  isSizeSetsError: boolean;
  sizePresets: SavedSize[];
  sizeSets: SizeSet[];
};

/**
 * Lives below `edit-canvas` and `ag-ui-chat` (which edit-canvas already imports) so both can
 * consume it without a cycle.
 */
export const useSavedSizesCatalog = ({
  enabled,
  staleTime,
  workspaceId,
}: UseSavedSizesCatalogParams): UseSavedSizesCatalogResult => {
  const sizePresetsQuery = usePrivateSizePresetsList({ workspaceId }, { enabled, staleTime });
  const sizeSetsQuery = usePrivateSizeSetsList({ workspaceId }, { enabled, staleTime });

  const sizePresets = useMemo(() => (sizePresetsQuery.data?.data ?? []).map(toSavedSize), [sizePresetsQuery.data]);
  const sizeSets = useMemo(() => (sizeSetsQuery.data?.data ?? []).map(toSizeSet), [sizeSetsQuery.data]);

  return {
    isError: sizePresetsQuery.isError || sizeSetsQuery.isError,
    isLoading: sizePresetsQuery.isLoading || sizeSetsQuery.isLoading,
    isSizePresetsError: sizePresetsQuery.isError,
    isSizePresetsLoading: sizePresetsQuery.isLoading,
    isSizeSetsError: sizeSetsQuery.isError,
    sizePresets,
    sizeSets,
  };
};
